import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './use-auth';
import { toast } from './use-toast';

export interface ExpenseCategory {
  id: string;
  company_id: string;
  name: string;
  icon: string;
  color: string;
  created_at: string;
}

export function useExpenseCategories() {
  const { activeCompany } = useAuth();

  return useQuery({
    queryKey: ['expense-categories', activeCompany?.id],
    queryFn: async () => {
      if (!activeCompany?.id) throw new Error('No active company');

      const { data, error } = await supabase
        .from('fleet_expense_categories')
        .select('*')
        .eq('company_id', activeCompany.id)
        .order('name');

      if (error) throw error;
      return data as ExpenseCategory[];
    },
    enabled: !!activeCompany?.id,
  });
}

export function useCreateCategory() {
  const queryClient = useQueryClient();
  const { activeCompany } = useAuth();

  return useMutation({
    mutationFn: async (category: Omit<ExpenseCategory, 'id' | 'company_id' | 'created_at'>) => {
      if (!activeCompany?.id) throw new Error('No active company');

      const { data, error } = await supabase
        .from('fleet_expense_categories')
        .insert([{ ...category, company_id: activeCompany.id }])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expense-categories'] });
      toast({ title: 'Categoria criada com sucesso' });
    },
    onError: (error) => {
      toast({
        title: 'Erro ao criar categoria',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<ExpenseCategory> & { id: string }) => {
      const { data, error } = await supabase
        .from('fleet_expense_categories')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expense-categories'] });
      toast({ title: 'Categoria atualizada com sucesso' });
    },
    onError: (error) => {
      toast({
        title: 'Erro ao atualizar categoria',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useDeleteCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('fleet_expense_categories')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expense-categories'] });
      toast({ title: 'Categoria excluída com sucesso' });
    },
    onError: (error) => {
      toast({
        title: 'Erro ao excluir categoria',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}
